/**
 * InputSystem - Handles touch/mouse input for player movement
 *
 * Features:
 * - Smooth horizontal drag/swipe control
 * - Works with both touch and mouse
 * - Movement bounded to screen edges
 * - < 100ms input lag (direct tracking, no artificial smoothing)
 */

import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { PointerDragTeardown, registerPointerDragHandlers } from './input/pointerBindings';

export interface InputSystemConfig {
  /** Player entity to control */
  player: Player;
  /** Function to clamp x position to track bounds */
  clampToTrack: (x: number, entityWidth: number) => number;
  /** Function to check if game is paused */
  getIsPaused: () => boolean;
  /** Horizontal keyboard movement speed in pixels per second */
  keyboardSpeedPxPerSecond?: number;
}

export class InputSystem {
  private static readonly DEFAULT_KEYBOARD_SPEED_PX_PER_SECOND = 500;

  private scene: Phaser.Scene;
  private player: Player;
  private clampToTrack: (x: number, entityWidth: number) => number;
  private getIsPaused: () => boolean;
  private keyboardSpeedPxPerSecond: number;

  /** Whether a drag is currently active */
  private isDragging: boolean = false;

  /** Offset between pointer and army center when drag started */
  private dragOffsetX: number = 0;

  // Bound handlers for proper cleanup (initialized in setupInput)
  private boundPointerDown!: (pointer: Phaser.Input.Pointer) => void;
  private boundPointerMove!: (pointer: Phaser.Input.Pointer) => void;
  private boundPointerUp!: (pointer: Phaser.Input.Pointer) => void;
  private removePointerHandlers: PointerDragTeardown | null = null;
  private leftKey: Phaser.Input.Keyboard.Key | null = null;
  private rightKey: Phaser.Input.Keyboard.Key | null = null;

  constructor(scene: Phaser.Scene, config: InputSystemConfig) {
    this.scene = scene;
    this.player = config.player;
    this.clampToTrack = config.clampToTrack;
    this.getIsPaused = config.getIsPaused;
    this.keyboardSpeedPxPerSecond = config.keyboardSpeedPxPerSecond
      ?? InputSystem.DEFAULT_KEYBOARD_SPEED_PX_PER_SECOND;

    this.setupInput();
  }

  /**
   * Sets up pointer (touch/mouse) event handlers
   */
  private setupInput(): void {
    // Bind handlers to preserve 'this' context
    this.boundPointerDown = this.onPointerDown.bind(this);
    this.boundPointerMove = this.onPointerMove.bind(this);
    this.boundPointerUp = this.onPointerUp.bind(this);

    this.removePointerHandlers = registerPointerDragHandlers(this.scene, {
      onPointerDown: this.boundPointerDown,
      onPointerMove: this.boundPointerMove,
      onPointerUp: this.boundPointerUp,
    });

    const keyboard = this.scene.input.keyboard;
    this.leftKey = keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT) ?? null;
    this.rightKey = keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT) ?? null;
    keyboard?.addCapture([
      Phaser.Input.Keyboard.KeyCodes.LEFT,
      Phaser.Input.Keyboard.KeyCodes.RIGHT,
    ]);
  }

  /**
   * Called when pointer is pressed down
   */
  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (this.getIsPaused()) return;

    // Start dragging
    this.isDragging = true;

    // Calculate offset from pointer to army center
    // This allows the army to stay in the same relative position to the finger
    this.dragOffsetX = this.player.getArmyX() - pointer.x;
  }

  /**
   * Called when pointer moves
   */
  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (this.getIsPaused()) return;
    if (!this.isDragging) return;

    // Calculate target position (pointer x + offset to maintain relative position)
    const targetX = pointer.x + this.dragOffsetX;

    // Clamp to track bounds, accounting for formation radius
    const formationRadius = this.player.getFormationRadius();
    const clampedX = this.clampToTrack(targetX, formationRadius * 2);

    // Move the army directly (no artificial smoothing to minimize input lag)
    this.player.setArmyX(clampedX);
  }

  /**
   * Called when pointer is released
   */
  private onPointerUp(_pointer: Phaser.Input.Pointer): void {
    this.isDragging = false;
    this.dragOffsetX = 0;
  }

  /**
   * Returns whether a drag is currently active
   */
  getIsDragging(): boolean {
    return this.isDragging;
  }

  /**
   * Handles keyboard movement for desktop gameplay.
   */
  update(deltaMs: number): void {
    if (this.getIsPaused() || this.isDragging || deltaMs <= 0) return;

    let direction = 0;
    if (this.leftKey?.isDown ?? false) direction -= 1;
    if (this.rightKey?.isDown ?? false) direction += 1;
    if (direction === 0) return;

    const movementPx = direction * this.keyboardSpeedPxPerSecond * (deltaMs / 1000);
    const targetX = this.player.getArmyX() + movementPx;

    const formationRadius = this.player.getFormationRadius();
    const clampedX = this.clampToTrack(targetX, formationRadius * 2);
    this.player.setArmyX(clampedX);
  }

  /**
   * Updates the player reference (if player is recreated)
   */
  setPlayer(player: Player): void {
    this.player = player;
  }

  /**
   * Cleanup when destroyed
   */
  destroy(): void {
    this.removePointerHandlers?.();
    this.removePointerHandlers = null;

    this.isDragging = false;
    this.leftKey = null;
    this.rightKey = null;
  }
}
